import { useState } from "react";
import {
  Phone, MessageCircle, Mail, MapPin, ShieldCheck, BadgeCheck, Award,
  Users, Globe2, Clock, FileCheck2, Scale, Languages, Stamp, Search,
  Plane, Building2, ArrowUpRight, Star, CheckCircle2, Sparkles, TrendingUp,
  FileBadge, ScrollText, Landmark, ChevronDown, Plus,
  Briefcase, GraduationCap, Heart, Building, UserCheck, Globe,
  FileText, BookOpen, Quote, Zap, Lock, RefreshCw, ThumbsUp, Headphones,
  Newspaper, Lightbulb, ChevronRight,
} from "lucide-react";
import { Link } from "@tanstack/react-router";

import { PortfolioShowcase } from "@/components/PortfolioShowcase";
import { COMPANY, BRANCHES } from "@/lib/company";
import { LAWYERS } from "@/lib/lawyers";
import { SERVICES, CATEGORIES, PRICING, type ServiceCategory } from "@/lib/services";
import {
  PHUKET_DISTRICTS, PHUKET_SUBDISTRICTS, PHUKET_TOURIST_ZONES,
  PHUKET_MARINAS, PHUKET_BUSINESS_HUBS, PHUKET_INTL_HOSPITALS, PHUKET_GOV_OFFICES,
} from "@/lib/phuket-areas";
import { PHUKET_HONORARY_CONSULS, PHUKET_CONSUL_TOTAL } from "@/lib/phuket-consuls";
import { DOCUMENT_GROUPS, TOTAL_DOC_TYPES } from "@/lib/documents";
import { LANGUAGES } from "@/lib/languages";
import { COUNTRIES } from "@/lib/countries";
import { EMBASSIES } from "@/lib/embassies";
import { OUTBOUND_VISA, INBOUND_THAI_VISA } from "@/lib/visa-types";
import { POLICE_CLEARANCE_TYPES } from "@/lib/police-clearance";
import { getActiveProvince } from "@/provinces/_registry";
type HomeFaqItem = { q: string; a: string };
import { HOME_ORDER_STEPS } from "@/content/home-steps";
import logo from "@/assets/logo-mark-256.webp";
import teamHero from "@/assets/team/team-bangkok-hero.webp";
import businessLicense from "@/assets/credentials/business-registration-official.webp";

const CATEGORY_ICONS: Record<ServiceCategory, typeof Stamp> = {
  notary: Stamp,
  translation: Languages,
  legalization: ScrollText,
  background: Search,
  "visa-marriage": Plane,
};

function toSafeStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function toSafeGroupedStringEntries(value: unknown): [string, string[]][] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value).map(([key, items]) => [key, toSafeStringArray(items)]);
}

export function HomeBody({ faq = [] }: { faq?: HomeFaqItem[] }) {
  return (
    <>
      <Hero />
      <TranslationLegalizationSpotlight />
      <AnswerEnginePanel />
      <TeamShowcase />
      <DashboardStats />
      <AeoQuickAnswer />
      <WhoWeServe />
      <Services />
      <DocumentTypes />
      <PoliceTypes />
      <BusinessLicense />
      <WhyChooseUs />
      <Pricing />
      <LanguagesCountries />
      <EmbassyDirectory />
      <VisaCatalog />
      <Lawyers />
      <Testimonials />
      <PortfolioShowcase />
      <Branches />
      <Coverage />
      <ProvinceCoverage />
      <Process />
      <Guarantees />
      <KnowledgeHub />
      <EntityPanel />
      <FaqSection faq={faq} />
      <FinalCta />
    </>
  );
}

/* ============== HERO ============== */

function Hero() {
  const { core: content } = getActiveProvince();
  const trust = content.trustStrip;
  return (
    <section
      className="relative isolate overflow-hidden text-primary-foreground"
      style={{ background: "var(--gradient-hero)" }}
    >
      {/* grid pattern */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.07]"
        style={{ backgroundImage: "var(--grid-pattern)", backgroundSize: "48px 48px" }}
      />
      {/* gold radial glow */}
      <div aria-hidden className="absolute -right-40 -top-40 -z-10 h-[600px] w-[600px] rounded-full bg-gold/20 blur-3xl" />
      <div aria-hidden className="absolute -left-40 bottom-0 -z-10 h-[500px] w-[500px] rounded-full bg-primary/40 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 py-16 md:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-gold backdrop-blur">
              <BadgeCheck className="h-3.5 w-3.5" />
              <span>จดทะเบียนเลขที่ {COMPANY.registrationNumber}</span>
              <span aria-hidden className="text-primary-foreground/70">•</span>
              <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Notarial Services Attorney</span>
            </div>
            <h1 className="mt-6 text-4xl font-black leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
              <span className="bg-gradient-to-r from-gold-bright via-gold to-gold-bright bg-clip-text text-transparent">
                {content.heroHeadline}
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/95 md:text-lg">
              {content.heroSubheadline}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="group inline-flex items-center gap-2 rounded-xl px-7 py-4 text-base font-bold text-gold-foreground shadow-[var(--shadow-gold-glow)] transition-all hover:scale-[1.02]"
                style={{ background: "var(--gradient-gold)" }}
              >
                <Phone className="h-5 w-5" />
                โทร {COMPANY.phone}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-gold/60 bg-gold/5 px-7 py-4 text-base font-bold text-gold backdrop-blur transition-all hover:bg-gold hover:text-gold-foreground"
              >
                <MessageCircle className="h-5 w-5" />
                LINE {COMPANY.lineId}
              </a>
            </div>
            <div className="mt-8 grid max-w-2xl grid-cols-1 gap-x-6 gap-y-3 text-sm text-primary-foreground/90 md:grid-cols-2">
              {trust.slice(0, 4).map((t, idx) => {
                const Icon = [Clock, MapPin, Globe2, ShieldCheck][idx] ?? CheckCircle2;
                return (
                  <div key={t} className="flex items-start gap-2">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <span>{t}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hero KPI card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div aria-hidden className="absolute -inset-4 rounded-3xl bg-gold/20 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/95 p-6 text-foreground shadow-2xl backdrop-blur-xl">
                <div className="flex items-center gap-3 border-b border-border pb-4">
                  <img src={logo} alt="Thai Notary Law logo" className="h-14 w-14" width="56" height="56" fetchPriority="high" />
                  <div>
                    <div className="text-sm font-extrabold text-primary">THAI NOTARY LAW</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gold">Phuket Island • Thailand</div>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" /> Verified
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <KpiTile label="เอกสารดำเนินการ" value="6,500+" sub="เคสในภูเก็ต" trend="Island" />
                  <KpiTile label="ทนายรับรอง" value="สภาทนายความฯ" sub="Notarial Services Attorney" trend="Licensed" />
                  <KpiTile label="กงสุลในเกาะ" value="22" sub="Honorary Consuls" trend="Unique" />
                  <KpiTile label="ครอบคลุม" value="17/17" sub="ตำบลทั่วภูเก็ต" trend="100%" />
                </div>

                <div className="mt-5 rounded-xl border border-gold/30 bg-gold/5 px-4 py-3">
                  <div className="flex items-start gap-2">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <p className="text-xs leading-relaxed text-foreground">
                      <strong className="text-primary">ปรึกษาฟรี:</strong> ส่งภาพเอกสาร — ทีมงานประเมินราคา &amp; ระยะเวลาให้ภายใน 30 นาที
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* wave divider */}
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-background/0" />
    </section>
  );
}

function KpiTile({ label, value, sub, trend }: { label: string; value: string; sub: string; trend: string }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/50 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-black text-primary">{value}</div>
      <div className="mt-0.5 flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground">{sub}</span>
        <span className="rounded-full bg-gold/15 px-1.5 text-[9px] font-bold text-gold-foreground">{trend}</span>
      </div>
    </div>
  );
}

/* ============== TEAM SHOWCASE ============== */

function TeamShowcase() {
  return (
    <section className="relative bg-background py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="ทีมงานมืออาชีพของเรา"
          title="ทนายความ Notarial Services Attorney และทีมงานครบทีม"
          sub="ทีมทนายความผู้ทำคำรับรองที่ขึ้นทะเบียนกับสภาทนายความฯ พร้อมทีมงานสนับสนุนผู้เชี่ยวชาญด้านเอกสารระหว่างประเทศ"
        />
        <div className="mt-10 overflow-hidden rounded-3xl border border-border shadow-[var(--shadow-elevated)]">
          <div className="relative aspect-[16/9] w-full bg-primary">
            <img
              src={teamHero}
              alt="ทีมทนาย Thai Notary Phuket ประจำเกาะภูเก็ต — ครอบคลุมป่าตอง กะตะ ลากูน่า อ่าวฉลอง"
              className="absolute inset-0 h-full w-full object-cover"
              width="1600"
              height="900"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/15 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold backdrop-blur">
                <Users className="h-3.5 w-3.5" /> Our Team • Phuket Island HQ
              </div>
              <h3 className="mt-3 max-w-3xl text-2xl font-black leading-tight text-primary-foreground md:text-4xl">
                ทีมทนายความและที่ปรึกษากฎหมายมืออาชีพ<br className="hidden md:block" />
                พร้อมดูแลคุณตลอด 24/7
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-primary-foreground/95 md:text-base">
                ดูแลงานเอกสารบนเกาะภูเก็ตเป็นประจำ — Expat, Digital Nomad, ลูกเรือยอตช์ และนักลงทุนอสังหาริมทรัพย์หลายสัญชาติ
              </p>
            </div>
          </div>
          {/* trust pill strip */}
          <div className="grid grid-cols-2 divide-x divide-y divide-border bg-card md:grid-cols-4 md:divide-y-0">
            {[
              { i: Scale, t: "สภาทนายความรับรอง", s: "ขึ้นทะเบียนถูกต้องตามข้อบังคับ พ.ศ. 2551" },
              { i: Award, t: "Notarial Attorney 6 ท่าน", s: "ผู้ทำคำรับรองลายมือชื่อและเอกสาร" },
              { i: FileCheck2, t: "ISO Workflow", s: "ขั้นตอนตรวจสอบเอกสารแบบมาตรฐาน" },
              { i: TrendingUp, t: "ทีมงานประจำภูเก็ต", s: "ดูแลเคสตั้งแต่ตรวจเอกสารถึงยื่นหน่วยงานปลายทาง" },
            ].map((x) => (
              <div key={x.t} className="flex items-start gap-3 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold">
                  <x.i className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-primary">{x.t}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{x.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== DASHBOARD STATS ============== */

function DashboardStats() {
  const stats = [
    { i: FileCheck2, v: "6,500+", l: "เอกสารในภูเก็ต", s: "+22% YoY", c: "from-blue-500/20 to-blue-500/5" },
    { i: Users, v: "6", l: "ทนาย Notary ขึ้นทะเบียน", s: "Active", c: "from-emerald-500/20 to-emerald-500/5" },
    { i: Globe2, v: "195", l: "ประเทศที่ส่งเอกสาร", s: "Worldwide", c: "from-amber-500/20 to-amber-500/5" },
    { i: Clock, v: "30 นาที", l: "เวลาตอบกลับเฉลี่ย", s: "8AM–8PM", c: "from-rose-500/20 to-rose-500/5" },
  ];
  return (
    <section className="bg-secondary/30 py-12">
      <div className="mx-auto max-w-7xl px-4">
        <div className="grid gap-4 md:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.l}
              className={`group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:shadow-[var(--shadow-card)]`}
            >
              <div className={`absolute inset-0 -z-10 bg-gradient-to-br ${s.c} opacity-60`} />
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-gold">
                  <s.i className="h-5 w-5" />
                </div>
                <span className="rounded-full border border-gold/30 bg-gold/10 px-2 py-0.5 text-[10px] font-bold text-gold-foreground">{s.s}</span>
              </div>
              <div className="mt-4 text-3xl font-black text-primary md:text-4xl">{s.v}</div>
              <div className="mt-1 text-xs font-medium text-muted-foreground md:text-sm">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== AEO QUICK ANSWER ============== */

function AeoQuickAnswer() {
  return AeoQuickAnswerInner();
}

/* ============== ANSWER ENGINE PANEL (AEO + GEO + Speakable) ============== */

function AnswerEnginePanel() {
  const facts: { k: string; v: string }[] = [
    { k: "ชื่อบริการ", v: "Thai Notary Phuket — Notary Public บนเกาะภูเก็ต" },
    { k: "ผู้ดำเนินการ", v: "Thai Notary Law & Service Co., Ltd. (ทะเบียน 04045565001923)" },
    { k: "ใบอนุญาต", v: "ทนาย Notarial Services Attorney ขึ้นทะเบียนสภาทนายความฯ (พ.ร.บ. 2528, ข้อบังคับ 2551)" },
    { k: "พื้นที่ให้บริการ", v: "3 อำเภอ 17 ตำบลทั่วจังหวัดภูเก็ต — เมือง, กะทู้, ถลาง" },
    { k: "สาขา", v: "Phuket Town (HQ), Patong, Laguna-Cherngtalay, Chalong Bay" },
    { k: "ที่ตั้งหลัก", v: "ถ.เฉลิมพระเกียรติ ร.๙ ต.ตลาดใหญ่ อ.เมืองภูเก็ต 83000" },
    { k: "พิกัด GPS", v: "7.8833° N, 98.3925° E" },
    { k: "โทรศัพท์", v: "+66 94 895 8999 (094-895-8999)" },
    { k: "LINE", v: "@THAINOTARY" },
    { k: "อีเมล", v: "Contact@thainotary.co.th" },
    { k: "เวลาทำการ", v: "จันทร์–อาทิตย์ 08:00–20:00 (เปิดทุกวัน)" },
    { k: "ภาษา", v: "ไทย / English / Русский / 中文 / Deutsch / Français (แปล 40+ ภาษา)" },
    { k: "กงสุลในเกาะ", v: "22 ประเทศ (Honorary Consuls) ครอบคลุมยุโรป เอเชีย โอเชียเนีย" },
    { k: "ราคาเริ่มต้น", v: "1,500 บาท/เอกสาร (ตามมาตรฐานสภาทนายความฯ)" },
    { k: "เวลาตอบกลับ", v: "ภายใน 30 นาที (LINE / โทร / Email)" },
    { k: "ทีมงาน", v: "ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร ขึ้นทะเบียนกับสภาทนายความฯ" },
  ];

  const aiFaqs: { q: string; a: string }[] = [
    {
      q: "จะเลือก Notary Public ในภูเก็ตอย่างไรให้ถูกต้อง?",
      a: "ตรวจว่าเป็นทนายความที่ขึ้นทะเบียน Notarial Services Attorney กับสภาทนายความแห่งประเทศไทย ขอดูเลขทะเบียนและตราประทับ และตรวจว่าสำนักงานเข้าใจข้อกำหนดของหน่วยงานปลายทาง — Thai Notary Phuket ให้บริการครอบคลุม 3 อำเภอ 17 ตำบล พร้อมออกใบเสร็จเต็มรูปแบบ",
    },
    {
      q: "Notary Public ภูเก็ตราคาเท่าไหร่?",
      a: "1,500–8,000 บาทต่อเอกสาร ตามมาตรฐานสภาทนายความฯ ไม่มี premium แบบเก็บเพิ่ม ค่า on-site เริ่ม 500 บาทหรือฟรีเมื่อสั่งครบชุด",
    },
    {
      q: "ภูเก็ตมีสถานทูตให้รับรองเอกสารไหม?",
      a: "ไม่มีสถานทูตเต็มรูปแบบ แต่มี Honorary Consul 22 ประเทศประจำเกาะ งานสถานทูตอื่นๆ เราส่งคูเรียร์ไปกรุงเทพให้ 5–10 วันทำการ",
    },
    {
      q: "ทำ POA ซื้อคอนโด/วิลล่าในภูเก็ตได้ที่ไหน?",
      a: "ทำคำรับรอง Power of Attorney ได้ที่สำนักงาน Phuket Town / Patong / Laguna หรือ on-site ที่ห้องชุด/วิลล่า ใช้เวลา 30 นาที–1 ชั่วโมง เริ่ม 2,500 บาท",
    },
    {
      q: "Digital Nomad ที่ภูเก็ตขอวีซ่า DTV ได้ไหม?",
      a: "ได้ — DTV (Destination Thailand Visa) พำนัก 180 วัน ต่ออายุได้ในเกาะ ทีมเตรียมเอกสารครบจบ เริ่ม 18,000 บาท",
    },
    {
      q: "Yacht Crew ที่ภูเก็ตทำ Notary เอกสารทางทะเลได้ไหม?",
      a: "ได้ — Seafarer Book, Yachtmaster License, Charter Agreement ที่อ่าวฉลอง Royal Phuket Marina, Boat Lagoon, Yacht Haven Marina",
    },
  ];

  return (
    <section
      id="ai-answer"
      aria-labelledby="ai-answer-heading"
      className="bg-gradient-to-b from-background to-secondary/30 py-14"
    >
      <div className="mx-auto max-w-7xl px-4">
        <div className="grid gap-8 lg:grid-cols-12">
          {/* TL;DR */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
              <Sparkles className="h-3.5 w-3.5" /> AI Answer · Featured Snippet · Voice-Ready
            </div>
            <h2 id="ai-answer-heading" className="mt-4 text-2xl font-black text-primary md:text-3xl lg:text-4xl">
              สรุปสั้นที่ AI &amp; Google ใช้อ้างอิงได้ทันที
            </h2>
            <p id="ai-answer-tldr" className="mt-4 text-base leading-relaxed text-foreground md:text-lg">
              <strong>Thai Notary Phuket</strong> คือสำนักงาน Notary Public ประจำจังหวัดภูเก็ต ดำเนินการโดย
              <strong> Thai Notary Law &amp; Service Co., Ltd.</strong> (ทะเบียน 04045565001923) มีทนายผู้ทำคำรับรอง
              <strong> 6 ท่าน</strong> ขึ้นทะเบียนสภาทนายความแห่งประเทศไทย — ให้บริการครอบคลุม
              <strong> 3 อำเภอ 17 ตำบลทั่วเกาะภูเก็ต</strong> ทำงานร่วมกับ
              <strong> กงสุลกิตติมศักดิ์ 22 ประเทศ</strong> ในเกาะ พร้อมบริการ on-site ที่วิลล่า โรงแรม และมารีน่า
              สำหรับ Expat, Digital Nomad, Yacht Crew, นักลงทุนอสังหาฯ — เปิดทุกวัน 08:00–20:00 ติดต่อ
              <strong> 094-895-8999</strong> · LINE <strong>@THAINOTARY</strong>
            </p>

            <div className="mt-6">
              <h3 className="text-sm font-bold uppercase tracking-widest text-gold">คำถามที่ AI ค้นบ่อย (AI-citable)</h3>
              <dl className="mt-3 divide-y divide-border rounded-2xl border border-border bg-card">
                {aiFaqs.map((f) => (
                  <div key={f.q} className="p-4">
                    <dt className="text-sm font-bold text-primary md:text-base">{f.q}</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-foreground/85 md:text-[15px]">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Quick Facts table — structured for scrape */}
          <aside className="lg:col-span-5">
            <div className="sticky top-20 overflow-hidden rounded-2xl border-2 border-gold/30 bg-card shadow-[var(--shadow-card)]">
              <div className="border-b border-border bg-primary px-5 py-3">
                <div className="text-[11px] font-bold uppercase tracking-widest text-gold">Quick Facts · Machine-Readable</div>
                <div className="mt-0.5 text-sm font-bold text-primary-foreground">Thai Notary Phuket — Entity Card</div>
              </div>
              <dl id="ai-answer-facts" className="divide-y divide-border text-sm">
                {facts.map((f) => (
                  <div key={f.k} className="grid grid-cols-12 gap-2 px-5 py-2.5">
                    <dt className="col-span-5 text-xs font-semibold text-muted-foreground">{f.k}</dt>
                    <dd className="col-span-7 text-xs font-medium text-foreground">{f.v}</dd>
                  </div>
                ))}
              </dl>
              <div className="border-t border-border bg-secondary/40 px-5 py-3 text-[11px] text-muted-foreground">
                ข้อมูลนี้ออกแบบมาให้ Google, ChatGPT, Claude, Perplexity, Gemini สามารถอ้างอิงได้โดยตรง
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function AeoQuickAnswerInner() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-14">
      <div className="relative overflow-hidden rounded-3xl border-2 border-gold/40 bg-gradient-to-br from-gold/10 via-card to-card p-6 shadow-[var(--shadow-card)] md:p-10">
        <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gold/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Sparkles className="h-3.5 w-3.5" /> AI Quick Answer / Featured Snippet
          </div>
          <h2 className="mt-4 text-2xl font-black text-primary md:text-3xl lg:text-4xl">
            ที่ไหนรับ Notary Public ภูเก็ตดีที่สุด?
          </h2>
          <p className="mt-4 max-w-4xl text-base leading-relaxed text-foreground md:text-lg">
            <strong>Thai Notary Phuket</strong> สำนักงาน Notary Public ประจำเกาะภูเก็ตที่ครอบคลุม
            <strong> ครบ 3 อำเภอ 17 ตำบล</strong> — ป่าตอง กะตะ กะรน ราไวย์ เชิงทะเล ลากูน่า ในยาง ไม้ขาว และ Phuket Old Town —
            ทำงานร่วมกับ <strong>กงสุลกิตติมศักดิ์ 22 ประเทศ</strong> ในเกาะ พร้อมบริการ on-site ที่วิลล่า โรงแรม และมารีน่า
            สำหรับ Expat, Digital Nomad, ลูกเรือยอตช์ และนักลงทุนอสังหาฯ — ราคาเริ่มต้น 1,500 บาท ติดต่อ
            <strong> 094-895-8999</strong> หรือ LINE <strong>@THAINOTARY</strong>
          </p>
          <ul className="mt-6 grid gap-2 md:grid-cols-2">
            {[
              "ทนายขึ้นทะเบียนสภาทนายความแห่งประเทศไทย ประจำภูเก็ต",
              "ครอบคลุม 3 อำเภอ • 17 ตำบล • 12 หาด • 6 มารีน่า",
              "On-site ถึงวิลล่า โรงแรม และเรือยอตช์ทั่วเกาะ",
              "กงสุลกิตติมศักดิ์ 22 ประเทศในภูเก็ต — ไม่ต้องบินไปกรุงเทพ",
              "เชี่ยวชาญ LTR / DTV / Retirement / Yacht Crew Visa",
              "ทีมพูดได้ ไทย / English / Русский / 中文 / Deutsch",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-foreground md:text-base">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ============== SERVICES ============== */

function Services() {
  return <ServicesInner />;
}

/* ============== TRANSLATION / NAATI / LEGALIZATION SPOTLIGHT ============== */

function TranslationLegalizationSpotlight() {
  const pillars = [
    {
      icon: Languages,
      eyebrow: "Certified Translation",
      title: "แปลรับรองภาษาอังกฤษ 40+ ภาษา",
      desc: "แปลโดยทีมนักแปลที่กรมการกงสุลและสถานทูตยอมรับ พร้อมคำรับรองและตราประทับมาตรฐานสากล ใช้ยื่นวีซ่า ตม. และศาลได้",
      price: "เริ่ม ฿500/หน้า",
      href: "/services/translation-certified-all",
      bullets: ["ไทย-อังกฤษ มาตรฐานกงสุล", "MoJ สำหรับเอกสารคดี/ศาล", "Sworn Translator เยอรมัน/ฝรั่งเศส/สเปน"],
    },
    {
      icon: BadgeCheck,
      eyebrow: "NAATI Translation",
      title: "แปล NAATI สำหรับออสเตรเลีย/นิวซีแลนด์",
      desc: "แปลโดยนักแปลที่ขึ้นทะเบียน NAATI ของออสเตรเลีย ยอมรับโดย Department of Home Affairs และ INZ โดยตรง — ใช้ยื่นวีซ่า PR, Skilled, Partner, Student",
      price: "เริ่ม ฿1,300/หน้า",
      href: "/services/naati-online",
      bullets: ["NAATI-certified Translator", "ส่งไฟล์ PDF + Hard Copy", "เหมาะ Visa 482/186/189/820/500"],
    },
    {
      icon: ScrollText,
      eyebrow: "MFA / Embassy Legalization",
      title: "รับรองกงสุล (MFA) + สถานทูตทุกประเทศ",
      desc: "ดำเนินการรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ และต่อรับรองสถานทูตปลายทางในกรุงเทพฯ ครบทุก Chain โดยไม่ต้องเดินทางออกจากภูเก็ต",
      price: "เริ่ม ฿2,500/ฉบับ",
      href: "/services/mfa-consular",
      bullets: ["MFA Legalization 3-5 วันทำการ", "Embassy ทุกประเทศในกรุงเทพฯ", "ส่งเอกสารคืนถึงบ้านในภูเก็ต"],
    },
  ];

  const steps = HOME_ORDER_STEPS;

  return (
    <section id="translation-legalization" className="relative isolate overflow-hidden py-20">
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-gold/5 via-background to-primary/5" />
      <div aria-hidden className="absolute -top-32 right-0 -z-10 h-[500px] w-[500px] rounded-full bg-gold/10 blur-3xl" />
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="Certified Translation • NAATI • Legalization"
          title="บริการแปล–รับรอง–สถานทูต ครบวงจรในภูเก็ต"
          sub="ตั้งแต่คำแปลรับรองมาตรฐานสากล นักแปล NAATI สำหรับออสเตรเลีย/นิวซีแลนด์ จนถึงรับรองกงสุล MFA และสถานทูตทุกประเทศ — ไม่ต้องเดินทางออกจากเกาะ"
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <a
                key={p.eyebrow}
                href={p.href}
                className="group relative flex flex-col overflow-hidden rounded-2xl border-2 border-border bg-card p-7 transition-all hover:-translate-y-1 hover:border-gold hover:shadow-[var(--shadow-gold-glow)]"
              >
                <div aria-hidden className="absolute right-0 top-0 h-32 w-32 -translate-y-16 translate-x-16 rounded-full bg-gold/15 blur-2xl" />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-gold shadow-[var(--shadow-card)] transition-transform group-hover:scale-110">
                  <Icon className="h-7 w-7" />
                </div>
                <div className="mt-5 text-[10px] font-bold uppercase tracking-widest text-gold">{p.eyebrow}</div>
                <h3 className="mt-1 text-xl font-black text-foreground group-hover:text-primary">{p.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-xs text-foreground">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                  <span className="text-base font-black text-primary">{p.price}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-primary group-hover:text-gold">
                    ดูบริการเต็ม <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>

        {/* Process strip */}
        <div className="mt-14 rounded-3xl border-2 border-gold/30 bg-card p-6 shadow-[var(--shadow-card)] md:p-10">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
                <Zap className="h-3.5 w-3.5" /> 4 ขั้นตอนสั่งงาน
              </div>
              <h3 className="mt-3 text-2xl font-black text-primary md:text-3xl">สั่งงานง่ายใน 4 ขั้นตอน — เริ่มจากภูเก็ต จบที่บ้านท่าน</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://wa.me/66948958999?text=สวัสดีครับ%20สนใจบริการแปล/NAATI/รับรองกงสุล"
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-gold-foreground shadow-[var(--shadow-gold-glow)] transition-transform hover:scale-[1.02]"
                style={{ background: "var(--gradient-gold)" }}
              >
                <MessageCircle className="h-4 w-4" /> ขอใบเสนอราคา (LINE/WhatsApp)
              </a>
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-primary/30 bg-secondary px-5 py-3 text-sm font-bold text-primary hover:border-gold hover:text-gold"
              >
                <Phone className="h-4 w-4" /> โทร 094-895-8999
              </a>
            </div>
          </div>
          <ol className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s) => (
              <li
                key={s.n}
                className="relative overflow-hidden rounded-2xl border border-border bg-secondary/40 p-5 transition-all hover:border-gold hover:bg-card"
              >
                <div className="text-3xl font-black text-gold">{s.n}</div>
                <h4 className="mt-2 text-base font-bold text-primary">{s.t}</h4>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{s.d}</p>
              </li>
            ))}
          </ol>

          <div className="mt-8 grid gap-3 rounded-2xl border border-border bg-secondary/30 p-5 text-sm md:grid-cols-3">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 shrink-0 text-gold" />
              <span><strong className="text-primary">ตอบกลับ 30 นาที</strong> ทุกวัน 08:00–20:00</span>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 shrink-0 text-gold" />
              <span><strong className="text-primary">รับเอกสารถึงที่</strong> ป่าตอง • กะตะ • ลากูน่า • ฉลอง • เชิงทะเล</span>
            </div>
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 shrink-0 text-gold" />
              <span><strong className="text-primary">รับประกันงาน</strong> ตีกลับฟรี ไม่มีค่าบริการเพิ่ม</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesInner() {
  const [tab, setTab] = useState<ServiceCategory>("notary");
  const filtered = SERVICES.filter((s) => s.category === tab);
  return (
    <section id="services" className="mx-auto max-w-7xl px-4 py-16">
      <SectionHead
        eyebrow="บริการทั้งหมด 29 รายการ"
        title="บริการ Notary & รับรองเอกสารครบวงจร"
        sub="ตั้งแต่รับรองโดยทนาย แปลทุกภาษา รับรองกงสุล/สถานทูต ตรวจประวัติ จนถึงวีซ่าและจดทะเบียนสมรส"
      />
      <div className="mt-10 flex flex-wrap justify-center gap-2">
        {CATEGORIES.map((c) => {
          const Icon = CATEGORY_ICONS[c.id];
          const active = tab === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setTab(c.id)}
              className={`group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                active
                  ? "bg-primary text-primary-foreground shadow-[var(--shadow-card)]"
                  : "border border-border bg-card text-foreground hover:border-gold hover:bg-gold/5"
              }`}
            >
              <Icon className="h-4 w-4" />
              {c.label}
              <span className={`text-[10px] font-medium ${active ? "text-gold" : "text-muted-foreground"}`}>{c.sub}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((s) => {
          const Icon = CATEGORY_ICONS[s.category];
          return (
            <a
              key={s.id}
              href={`https://thainotary.co.th${s.externalPath}`}
              target="_blank"
              rel="noopener"
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-gold hover:shadow-[var(--shadow-gold-glow)]"
            >
              <div aria-hidden className="absolute right-0 top-0 h-24 w-24 -translate-y-12 translate-x-12 rounded-full bg-gold/10 blur-2xl transition-opacity group-hover:opacity-100" />
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-gold transition-transform group-hover:scale-110">
                <Icon className="h-6 w-6" />
              </div>
              <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-gold">{s.titleEn}</div>
              <h3 className="mt-1 text-lg font-bold text-foreground group-hover:text-primary">{s.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              {s.priceFrom && (
                <div className="mt-4 inline-flex items-baseline gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-xs">
                  <span className="text-muted-foreground">เริ่มต้น</span>
                  <span className="text-base font-black text-primary">฿{s.priceFrom.toLocaleString()}</span>
                  {s.priceTo && <span className="text-muted-foreground">– ฿{s.priceTo.toLocaleString()}</span>}
                </div>
              )}
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-primary group-hover:text-gold">
                รายละเอียดเต็ม <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}

/* ============== BUSINESS LICENSE ============== */

function BusinessLicense() {
  return (
    <section
      id="license"
      className="relative isolate overflow-hidden py-20 text-primary-foreground"
      style={{ background: "var(--gradient-hero)" }}
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.06]"
        style={{ backgroundImage: "var(--grid-pattern)", backgroundSize: "40px 40px" }}
      />
      <div aria-hidden className="absolute -left-32 top-1/3 -z-10 h-[500px] w-[500px] rounded-full bg-gold/15 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          {/* License image left */}
          <div className="lg:col-span-6">
            <div className="relative">
              <div aria-hidden className="absolute -inset-6 rounded-3xl bg-gold/20 blur-2xl" />
              <div
                className="relative overflow-hidden rounded-2xl border-4 border-gold/60 bg-card shadow-[var(--shadow-elevated)]"
                style={{ background: "var(--gradient-gold)" }}
              >
                <div className="relative">
                  <img
                    src={businessLicense}
                    alt="หนังสือรับรองการจดทะเบียนบริษัท Thai Notary Law & Service Co., Ltd. เลขทะเบียน 04045565001923"
                    className="w-full"
                    loading="lazy"
                    decoding="async"
                    width="1200"
                    height="1697"
                  />
                  <div className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-emerald-500/95 px-3 py-1.5 text-xs font-black uppercase tracking-wider text-white shadow-lg backdrop-blur">
                    <BadgeCheck className="h-4 w-4" /> Verified
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Info right */}
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-gold backdrop-blur">
              <FileBadge className="h-3.5 w-3.5" /> Official Business Registration
            </div>
            <h2 className="mt-5 text-3xl font-black leading-tight md:text-5xl">
              จดทะเบียน<span className="text-gold">ถูกต้อง</span>ตามกฎหมาย<br />
              ตรวจสอบได้ที่กรมพัฒนาธุรกิจการค้า
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-primary-foreground/95">
              <strong>{COMPANY.legalName}</strong> จดทะเบียนเป็นนิติบุคคลถูกต้องตามประมวลกฎหมายแพ่งและพาณิชย์
              พร้อมสำนักงานสาขาประจำเกาะภูเก็ต — ลูกค้ามั่นใจได้ทุกธุรกรรมและทุกเอกสาร
            </p>

            <dl className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                { k: "เลขทะเบียนนิติบุคคล", v: COMPANY.registrationNumber, i: FileBadge },
                { k: "วันที่จดทะเบียน", v: "15 มีนาคม 2565", i: Landmark },
                { k: "หน่วยงานออกหนังสือ", v: "กรมพัฒนาธุรกิจการค้า", i: ShieldCheck },
                { k: "ที่ตั้งสำนักงานหลัก", v: "Phuket Town • อำเภอเมืองภูเก็ต", i: MapPin },
              ].map((x) => (
                <div key={x.k} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold/20 text-gold">
                    <x.i className="h-4 w-4" />
                  </div>
                  <div>
                    <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{x.k}</dt>
                    <dd className="mt-0.5 text-sm font-bold text-primary-foreground">{x.v}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://datawarehouse.dbd.go.th/"
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold text-gold-foreground shadow-[var(--shadow-gold-glow)] transition-transform hover:scale-[1.02]"
                style={{ background: "var(--gradient-gold)" }}
              >
                <ShieldCheck className="h-4 w-4" />
                ตรวจสอบที่ DBD <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-white/20 bg-white/5 px-6 py-3.5 text-sm font-bold text-primary-foreground backdrop-blur hover:border-gold hover:text-gold"
              >
                <Phone className="h-4 w-4" /> สอบถามเพิ่มเติม
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== PRICING ============== */

function Pricing() {
  return (
    <section id="pricing" className="bg-background py-16">
      <div className="mx-auto max-w-5xl px-4">
        <SectionHead
          eyebrow="ราคาโปร่งใส ไม่มีค่าใช้จ่ายแฝง"
          title="ตารางราคาบริการ (ปี 2026)"
          sub="ราคาขึ้นกับประเภทเอกสารและจำนวน — แจ้งใบเสนอราคาฟรีก่อนเริ่มงานทุกครั้ง"
        />
        <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-card)]">
          <table className="w-full text-left text-sm">
            <thead className="bg-primary text-primary-foreground">
              <tr>
                <th className="px-6 py-4 font-bold">รายการบริการ</th>
                <th className="px-6 py-4 text-right font-bold">ราคา (บาท)</th>
              </tr>
            </thead>
            <tbody>
              {PRICING.map((p, i) => (
                <tr key={p.item} className={`border-t border-border transition-colors hover:bg-gold/5 ${i % 2 === 0 ? "bg-background" : "bg-secondary/40"}`}>
                  <td className="px-6 py-4 font-medium text-foreground">{p.item}</td>
                  <td className="px-6 py-4 text-right font-bold text-primary">{p.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          * บริการอื่นๆ ที่ไม่ได้อยู่ในตาราง สอบถามเจ้าหน้าที่ที่ {COMPANY.phone} หรือ LINE {COMPANY.lineId}
        </p>
      </div>
    </section>
  );
}

/* ============== LAWYERS ============== */

function Lawyers() {
  const [open, setOpen] = useState<string | null>(null);
  const lic = LAWYERS.find((l) => l.id === open);
  return (
    <section id="lawyers" className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="ทีมทนายความผู้ทำคำรับรอง"
          title="6 ทนายขึ้นทะเบียนสภาทนายความ"
          sub="ทุกท่านมีใบอนุญาตเป็นทนายความและขึ้นทะเบียนเป็นทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ตามข้อบังคับฯ พ.ศ. 2551 — คลิกเพื่อดูใบอนุญาตจริง"
        />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {LAWYERS.map((l) => (
            <button
              key={l.id}
              onClick={() => setOpen(l.id)}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card text-left shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold hover:shadow-[var(--shadow-gold-glow)]"
            >
              <div className="relative aspect-square w-full overflow-hidden bg-primary">
                <img
                  src={l.portrait}
                  alt={`${l.name} — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="400"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/90 to-transparent p-3">
                  <span className="inline-flex items-center gap-1 rounded-full bg-gold/90 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-gold-foreground">
                    <BadgeCheck className="h-3 w-3" /> Notarial Attorney
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-foreground">{l.name} <span className="text-sm font-medium text-muted-foreground">/ {l.nameEn}</span></h3>
                <p className="mt-1 text-xs text-muted-foreground">{l.titleTh}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {l.specialties.map((sp) => (
                    <span key={sp} className="rounded-full bg-gold/10 px-2.5 py-0.5 text-[10px] font-semibold text-primary">{sp}</span>
                  ))}
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-primary group-hover:text-gold">
                  <FileBadge className="h-3.5 w-3.5" /> ดูใบอนุญาตจริง <ArrowUpRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {lic && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur" onClick={() => setOpen(null)}>
          <div className="relative max-h-[90vh] max-w-3xl overflow-auto rounded-2xl bg-card p-3 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setOpen(null)}
              className="absolute right-4 top-4 z-10 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground hover:bg-gold hover:text-gold-foreground"
            >
              ✕ ปิด
            </button>
            <img
              src={lic.license}
              alt={`ใบอนุญาตว่าความ ${lic.name} — สภาทนายความแห่งประเทศไทย`}
              className="w-full rounded-xl"
              loading="lazy"
              decoding="async"
            />
            <div className="px-3 py-3 text-center text-sm">
              <div className="font-bold text-primary">{lic.name} / {lic.nameEn}</div>
              <div className="text-xs text-muted-foreground">ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร — สภาทนายความแห่งประเทศไทย</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ============== BRANCHES ============== */

function Branches() {
  return (
    <section id="branches" className="bg-background py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="4 สำนักงานทั่วเกาะภูเก็ต"
          title="สำนักงาน Thai Notary Phuket"
          sub="Phuket Town • Patong • Cherngtalay (Laguna) • Chalong Bay — ครอบคลุมทั้ง 3 อำเภอของจังหวัดภูเก็ต"
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {BRANCHES.map((b) => (
            <div
              key={b.id}
              className={`group relative overflow-hidden rounded-2xl border bg-card p-6 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 ${
                b.isPrimary ? "border-2 border-gold" : "border-border hover:border-gold"
              }`}
            >
              {b.isPrimary && (
                <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-gold-foreground">
                  <Building2 className="h-3 w-3" /> สำนักงานใหญ่
                </div>
              )}
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-gold">
                <MapPin className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-extrabold text-primary">{b.name}</h3>
              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-gold">{b.nameEn}</div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{b.address}</p>
              <a href={b.mapUrl} target="_blank" rel="noopener" className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-primary hover:text-gold">
                เปิด Google Maps <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== COVERAGE ============== */

function Coverage() {
  return (
    <section id="coverage" className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="พื้นที่บริการ"
          title="ครอบคลุมทุกหาด ทุกมารีน่า ทุกย่านเศรษฐกิจของภูเก็ต"
          sub="3 อำเภอ • 17 ตำบล • 12 โซนท่องเที่ยว • 6 มารีน่า • ย่านธุรกิจหลักทั่วเกาะ — ทีมเดินทาง on-site ถึงวิลล่า โรงแรม และเรือยอตช์"
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <CoverageCard title="โซนท่องเที่ยวหลักทั่วเกาะภูเก็ต" items={PHUKET_TOURIST_ZONES} icon={MapPin} />
          <CoverageCard title="มารีน่า & ท่าเทียบเรือยอตช์" items={PHUKET_MARINAS} icon={Building2} />
          <CoverageCard title="ย่านธุรกิจ & ศูนย์การค้า" items={PHUKET_BUSINESS_HUBS} icon={Landmark} />
          <CoverageCard title="ตำบลในจังหวัดภูเก็ต (3 อำเภอ)" items={PHUKET_SUBDISTRICTS} icon={MapPin} />
        </div>
      </div>
    </section>
  );
}

function CoverageCard({ title, items, icon: Icon }: { title: string; items: readonly string[]; icon: typeof MapPin }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-gold">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-lg font-bold text-primary">{title}</h3>
        <span className="ml-auto rounded-full bg-gold/15 px-2.5 py-0.5 text-[10px] font-bold text-gold-foreground">{items.length}</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {items.map((i) => (
          <span key={i} className="rounded-md border border-border bg-secondary/60 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-gold hover:bg-gold/10">{i}</span>
        ))}
      </div>
    </div>
  );
}

/* ============== PROCESS ============== */

function Process() {
  const steps = [
    { n: "01", i: MessageCircle, t: "ติดต่อแจ้งความต้องการ", d: "โทร 094-895-8999 หรือ LINE @THAINOTARY ส่งภาพเอกสาร" },
    { n: "02", i: FileCheck2, t: "รับใบเสนอราคา", d: "ทีมงานประเมินภายใน 30 นาที ราคาชัดเจน" },
    { n: "03", i: ScrollText, t: "ส่งเอกสารต้นฉบับ", d: "เดินทางมาที่สำนักงาน / นัดนอกสถานที่ / ส่งทางไปรษณีย์" },
    { n: "04", i: Stamp, t: "ดำเนินการ Notary / แปล", d: "ทนายลงนามรับรอง / นักแปลแปลและรับรอง" },
    { n: "05", i: ShieldCheck, t: "ยื่นรับรอง MFA / สถานทูต", d: "(ถ้าจำเป็น) ทีมงานนำไปยื่นที่กรมการกงสุล / สถานทูต" },
    { n: "06", i: Plane, t: "จัดส่งเอกสารถึงมือ", d: "ส่งทั่วไทย 100 บาท ทั่วโลก 2,500 บาท หรือรับเองที่สำนักงาน" },
  ];
  return (
    <section className="relative isolate overflow-hidden py-20 text-primary-foreground" style={{ background: "var(--gradient-hero)" }}>
      <div aria-hidden className="absolute inset-0 -z-10 opacity-[0.06]" style={{ backgroundImage: "var(--grid-pattern)", backgroundSize: "40px 40px" }} />
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead eyebrow="ขั้นตอนการให้บริการ" title="6 ขั้นตอน ครบจบในที่เดียว" sub="ออกแบบกระบวนการทำงานให้กระชับ โปร่งใส และตรวจสอบได้ทุกขั้นตอน" dark />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-gold/60 hover:bg-white/10">
              <div className="flex items-start justify-between">
                <div className="text-5xl font-black leading-none text-gold/30">{s.n}</div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold text-gold-foreground">
                  <s.i className="h-5 w-5" />
                </div>
              </div>
              <h3 className="mt-4 text-lg font-bold text-primary-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-primary-foreground/90">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== ENTITY PANEL ============== */

function EntityPanel() {
  return (
    <section className="bg-background py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="Entity Panel / Knowledge Graph"
          title="ข้อมูลบริษัทแบบ Machine-Readable"
          sub="สำหรับ Google Knowledge Panel, AI Overview และ Search Engine ทุกตัว"
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] lg:col-span-2">
            <h3 className="text-base font-bold text-primary">เกี่ยวกับ Thai Notary Phuket</h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground">
              <strong>{COMPANY.legalName}</strong> (เลขทะเบียนนิติบุคคล {COMPANY.registrationNumber})
              สำนักงาน Notary Public ประจำเกาะภูเก็ต ดำเนินงานโดยทีมทนายความที่ขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย
              ให้บริการเฉพาะกลุ่มลูกค้าในจังหวัดภูเก็ต — Expat, Digital Nomad (DTV/LTR), ลูกเรือยอตช์ที่ Royal Phuket Marina /
              Boat Lagoon / Yacht Haven, นักลงทุนอสังหาฯ (วิลล่า/คอนโด) และเจ้าของกิจการโรงแรม/ทัวร์/ดำน้ำ
              ครอบคลุม 3 อำเภอ 17 ตำบล 12 หาด 6 มารีน่า — พร้อมประสานกงสุลกิตติมศักดิ์ 22 ประเทศที่ตั้งสำนักงานในเกาะ
            </p>
            <h4 className="mt-5 text-sm font-bold text-primary">บริการหลัก (อ้างอิงจากเว็บไซต์ในเครือ)</h4>
            <ul className="mt-2 grid grid-cols-1 gap-1 text-xs text-foreground md:grid-cols-2">
              {SERVICES.slice(0, 14).map((s) => (
                <li key={s.id}>
                  • <a href={`https://thainotary.co.th${s.externalPath}`} target="_blank" rel="noopener" className="hover:underline">{s.title}</a>
                  <sup className="text-gold"> [↗]</sup>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-gold bg-gold/5 p-6 shadow-[var(--shadow-card)]">
            <h3 className="text-base font-bold text-primary">ข้อมูลอ้างอิงด่วน</h3>
            <dl className="mt-4 space-y-3 text-xs">
              <Field k="ชื่อทางการ" v={COMPANY.legalName} />
              <Field k="แบรนด์" v={COMPANY.brandName} />
              <Field k="ทะเบียน" v={COMPANY.registrationNumber} />
              <Field k="ประเภทธุรกิจ" v="LegalService / Notary Public / Translation" />
              <Field k="พื้นที่ให้บริการ" v="จังหวัดภูเก็ต (3 อำเภอ • 17 ตำบล • 12 หาด • 6 มารีน่า)" />
              <Field k="สำนักงานในเกาะ" v="Phuket Town • Patong • Laguna (Cherngtalay) • Chalong Bay" />
              <Field k="เครือข่ายกงสุล" v={`กงสุลกิตติมศักดิ์ ${PHUKET_CONSUL_TOTAL} ประเทศในภูเก็ต`} />
              <Field k="เวลาทำการ" v={COMPANY.hours} />
              <Field k="สถานะทนาย" v={COMPANY.trustLine} />
              <Field k="ภาษาที่ให้บริการ" v="ไทย / English / Русский / 中文 / Deutsch / Français + 40 ภาษา" />
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{k}</dt>
      <dd className="text-sm font-semibold text-foreground">{v}</dd>
    </div>
  );
}

/* ============== FAQ ============== */

function FaqSection({ faq }: { faq: HomeFaqItem[] }) {
  return (
    <section className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-4xl px-4">
        <SectionHead
          eyebrow="คำถามที่พบบ่อย"
          title="FAQ – Notary Public ภูเก็ต"
          sub="คำตอบที่ตรงและสั้น สำหรับลูกค้าคนไทยและชาวต่างชาติ"
        />
        <div className="mt-10 space-y-3">
          {faq.map((f) => (
            <details key={f.q} className="group rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-card)] open:border-gold">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-bold text-foreground">
                <span>{f.q}</span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold transition-transform group-open:rotate-45">
                  <Plus className="h-4 w-4" />
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== FINAL CTA ============== */

function FinalCta() {
  return (
    <section className="relative isolate overflow-hidden py-20 text-primary-foreground" style={{ background: "var(--gradient-hero)" }}>
      <div aria-hidden className="absolute inset-0 -z-10 opacity-[0.06]" style={{ backgroundImage: "var(--grid-pattern)", backgroundSize: "40px 40px" }} />
      <div aria-hidden className="absolute -right-32 -top-32 -z-10 h-[500px] w-[500px] rounded-full bg-gold/20 blur-3xl" />
      <div className="mx-auto max-w-4xl px-4 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-gold">
          <Sparkles className="h-3.5 w-3.5" /> พร้อมเริ่มต้น
        </div>
        <h2 className="mt-5 text-3xl font-black md:text-5xl lg:text-6xl">
          ปรึกษาฟรี <span className="bg-gradient-to-r from-gold-bright via-gold to-gold-bright bg-clip-text text-transparent">ตอบกลับใน 30 นาที</span>
        </h2>
        <p className="mt-5 text-base text-primary-foreground/95 md:text-lg">
          ทีมงาน Thai Notary Phuket พร้อมให้คำปรึกษาทุกขั้นตอน — Notary, แปล,
          รับรองกงสุลกิตติมศักดิ์ในเกาะ, ประสาน ตม. ภูเก็ต และสำนักงานที่ดินจังหวัดภูเก็ต
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            className="inline-flex items-center gap-2 rounded-xl px-7 py-4 text-base font-bold text-gold-foreground shadow-[var(--shadow-gold-glow)] transition-transform hover:scale-[1.02]"
            style={{ background: "var(--gradient-gold)" }}
          >
            <Phone className="h-5 w-5" /> โทร {COMPANY.phone}
          </a>
          <a
            href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 rounded-xl border-2 border-gold bg-transparent px-7 py-4 text-base font-bold text-gold transition-colors hover:bg-gold hover:text-gold-foreground"
          >
            <MessageCircle className="h-5 w-5" /> LINE {COMPANY.lineId}
          </a>
          <a
            href={`mailto:${COMPANY.email}`}
            className="inline-flex items-center gap-2 rounded-xl border-2 border-white/20 bg-white/5 px-7 py-4 text-base font-bold text-primary-foreground backdrop-blur transition-colors hover:border-gold hover:text-gold"
          >
            <Mail className="h-5 w-5" /> {COMPANY.email}
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============== SHARED ============== */

function SectionHead({ eyebrow, title, sub, dark }: { eyebrow: string; title: string; sub: string; dark?: boolean }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
        <ChevronDown className="h-3 w-3 rotate-[-90deg]" /> {eyebrow}
      </div>
      <h2 className={`mt-4 text-3xl font-black tracking-tight md:text-4xl lg:text-5xl ${dark ? "text-primary-foreground" : "text-primary"}`}>{title}</h2>
      {sub && <p className={`mx-auto mt-4 max-w-2xl text-sm md:text-base ${dark ? "text-primary-foreground/90" : "text-muted-foreground"}`}>{sub}</p>}
    </div>
  );
}

/* ============== WHO WE SERVE ============== */

function WhoWeServe() {
  const personas = [
    { i: GraduationCap, t: "นักเรียน / นักศึกษา", d: "เอกสารยื่นเรียนต่อต่างประเทศ — Transcript, Diploma, ปพ.1, Recommendation Letter", tag: "Education" },
    { i: Briefcase, t: "คนทำงานต่างประเทศ", d: "Police Clearance, ใบรับรองประสบการณ์ทำงาน, วุฒิการศึกษา — รับรองครบจบ", tag: "Career" },
    { i: Heart, t: "คู่รักไทย–ต่างชาติ", d: "เอกสารจดทะเบียนสมรส รับรองโสด รับรองสถานภาพ — ทุกสัญชาติ", tag: "Marriage" },
    { i: Building, t: "ผู้ประกอบการ / บริษัท", d: "หนังสือมอบอำนาจ, สัญญาธุรกิจ, BOI, จดทะเบียนต่างประเทศ", tag: "Business" },
    { i: UserCheck, t: "Expat ในไทย", d: "Notary for Foreigners, Affidavit, Power of Attorney, MFA legalization", tag: "Expat" },
    { i: Globe, t: "คนไทยในต่างประเทศ", d: "รับรองผ่านสถานทูตไทย แปลและส่งกลับไทย — ประสานงานทางไกล", tag: "Overseas Thai" },
  ];
  return (
    <section className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="กลุ่มลูกค้าที่เราดูแล"
          title="ออกแบบบริการให้ตรงทุกความต้องการ"
          sub="ไม่ว่าคุณจะเป็นนักเรียน คนทำงาน คู่รักต่างชาติ หรือบริษัท — เรามีโซลูชันเฉพาะกลุ่มที่พร้อมใช้งานทันที"
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {personas.map((p) => (
            <div
              key={p.t}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-gold shadow-lg">
                  <p.i className="h-7 w-7" />
                </div>
                <span className="rounded-full border border-gold/30 bg-gold/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold-foreground">{p.tag}</span>
              </div>
              <h3 className="mt-5 text-lg font-bold text-primary">{p.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
              <a href={`tel:${COMPANY.phoneIntl}`} className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-primary group-hover:text-gold">
                ปรึกษาฟรี <ChevronRight className="h-3.5 w-3.5" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== DOCUMENT TYPES ============== */

const DOC_GROUP_ICONS = [FileText, GraduationCap, Briefcase, Scale, Heart, Search, Building, Plane];

function DocumentTypes() {
  return (
    <section id="documents" className="bg-background py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow={`ประเภทเอกสารที่รับ — ${TOTAL_DOC_TYPES}+ รายการ`}
          title={`รับรองเอกสารกว่า ${TOTAL_DOC_TYPES}+ ประเภท แบ่งเป็น ${DOCUMENT_GROUPS.length} หมวดหลัก`}
          sub="ครอบคลุมตั้งแต่เอกสารส่วนบุคคล การศึกษา ธุรกิจ กฎหมาย การแพทย์ ตรวจประวัติ อสังหาฯ จนถึงการทำงาน/วีซ่า — หากไม่เห็นเอกสารที่คุณต้องการ ส่งภาพมาทาง LINE ทีมงานประเมินทันที"
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {DOCUMENT_GROUPS.map((g, idx) => {
            const Icon = DOC_GROUP_ICONS[idx] ?? FileText;
            return (
              <div key={g.id} className="group rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold hover:shadow-[var(--shadow-gold-glow)]">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-gold transition-transform group-hover:scale-110">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="ml-auto rounded-full bg-gold/15 px-2.5 py-0.5 text-[10px] font-bold text-gold-foreground">{g.items.length} รายการ</span>
                </div>
                <h3 className="mt-4 text-lg font-bold text-primary">{g.title}</h3>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold">{g.titleEn}</div>
                <ul className="mt-4 space-y-1.5">
                  {g.items.slice(0, 7).map((it) => (
                    <li key={it} className="flex items-start gap-2 text-[13px] leading-snug text-foreground">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
                      <span>{it}</span>
                    </li>
                  ))}
                  {g.items.length > 7 && (
                    <li className="pt-1 text-[12px] font-bold">
                      <Link to="/catalog" className="text-primary hover:text-gold">
                        + อีก {g.items.length - 7} รายการ — ดูรายการเอกสารทั้งหมด →
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-sm text-muted-foreground">
          ไม่เห็นเอกสารที่คุณต้องการ? <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} className="font-bold text-primary hover:text-gold">สอบถามผ่าน LINE {COMPANY.lineId} →</a>
        </p>
      </div>
    </section>
  );
}

/* ============== WHY CHOOSE US ============== */

function WhyChooseUs() {
  const reasons = [
    { i: Scale, t: "ทนายขึ้นทะเบียนจริง", d: "6 ทนาย Notarial Services Attorney ขึ้นทะเบียนกับสภาทนายความฯ ตรวจสอบใบอนุญาตได้ทุกท่าน", c: "from-blue-500/15 to-blue-500/5" },
    { i: Zap, t: "ตอบกลับใน 30 นาที", d: "Call Center และ LINE @THAINOTARY เปิดทุกวัน 08:00–20:00 ตอบไว ประเมินราคาทันที", c: "from-amber-500/15 to-amber-500/5" },
    { i: Lock, t: "รักษาความลับตามสัญญา NDA", d: "ทำสัญญารักษาความลับทุกเคส เอกสารลูกค้าจัดเก็บตามมาตรฐานวิชาชีพของสภาทนายความฯ", c: "from-emerald-500/15 to-emerald-500/5" },
    { i: Globe2, t: "เครือข่าย 195 ประเทศ", d: "ประสานงานสถานทูต/กงสุลครบทุกประเทศ พร้อมจัดส่ง DHL/FedEx ทั่วโลก", c: "from-rose-500/15 to-rose-500/5" },
    { i: RefreshCw, t: "แก้ไขงานให้ฟรีเมื่อเป็นข้อผิดพลาดของเรา", d: "หากเอกสารถูกตีกลับเพราะความผิดพลาดในงานของเรา เราแก้ไขและยื่นใหม่ให้โดยไม่คิดค่าบริการ ทั้งนี้ผลการพิจารณาเป็นดุลพินิจของหน่วยงานปลายทาง", c: "from-violet-500/15 to-violet-500/5" },
    { i: ThumbsUp, t: "ราคาโปร่งใส", d: "แจ้งราคาเป็นลายลักษณ์อักษรก่อนเริ่มงานทุกครั้ง ไม่มีค่าใช้จ่ายแฝง", c: "from-cyan-500/15 to-cyan-500/5" },
  ];
  return (
    <section className="bg-background py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="ทำไมต้อง Thai Notary Law"
          title="6 เหตุผลที่ลูกค้าเลือกเรา"
          sub="ความแตกต่างที่ทำให้ Expat, Digital Nomad และนักลงทุนในภูเก็ตเลือกเรา — แนะนำต่อกันในชุมชน Expat ป่าตอง กะตะ ลากูน่า และเชิงทะเล"
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {reasons.map((r, i) => (
            <div
              key={r.t}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-gold hover:shadow-[var(--shadow-gold-glow)]"
            >
              <div className={`absolute inset-0 -z-10 bg-gradient-to-br ${r.c} opacity-50`} />
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-gold">
                  <r.i className="h-6 w-6" />
                </div>
                <span className="text-3xl font-black text-gold/40">0{i + 1}</span>
              </div>
              <h3 className="mt-4 text-lg font-bold text-primary">{r.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== LANGUAGES & COUNTRIES ============== */

function LanguagesCountries() {
  const countryEntries = toSafeGroupedStringEntries(COUNTRIES);
  const totalCountries = countryEntries.reduce((sum, [, list]) => sum + list.length, 0);
  return (
    <section id="languages" className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="ภาษา & ประเทศปลายทาง"
          title={`แปลและรับรองได้ ${LANGUAGES.length}+ ภาษา • ส่งเอกสารถึง ${totalCountries}+ ประเทศ`}
          sub="ทีมนักแปลผู้เชี่ยวชาญและเครือข่ายสถานทูตครอบคลุมทุกทวีป — ครบทุกภาษาหลักที่คนไทยและชาวต่างชาติต้องการ"
        />

        {/* Languages grid */}
        <div className="mt-10 rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
          <div className="flex flex-wrap items-center gap-2">
            <Languages className="h-5 w-5 text-gold" />
            <h3 className="text-lg font-bold text-primary">ภาษาที่รับแปลและรับรอง</h3>
            <span className="ml-auto rounded-full bg-gold/15 px-3 py-1 text-xs font-bold text-gold-foreground">{LANGUAGES.length} ภาษา</span>
          </div>
          <div className="mt-5 grid gap-2.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {LANGUAGES.slice(0, 16).map((l) => (
              <div key={l.name} className="flex items-center gap-3 rounded-xl border border-border bg-secondary/50 p-2.5 transition-colors hover:border-gold hover:bg-gold/5">
                <div className="text-2xl">{l.flag}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-foreground">{l.name}</div>
                  <div className="text-[10px] text-muted-foreground">{l.th} • {l.cert}</div>
                </div>
              </div>
            ))}
          </div>
          {LANGUAGES.length > 16 && (
            <div className="mt-4 text-sm font-bold">
              <Link to="/translation" className="text-primary hover:text-gold">
                + อีก {LANGUAGES.length - 16} ภาษา — ดูภาษาที่รับแปลทั้งหมด →
              </Link>
            </div>
          )}
        </div>

        {/* Countries by region */}
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {countryEntries.map(([region, list]) => (
            <div key={region} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-2">
                <Globe2 className="h-4 w-4 text-gold" />
                <h4 className="text-sm font-bold text-primary">{region}</h4>
                <span className="ml-auto text-[10px] font-bold text-muted-foreground">{list.length} ประเทศ</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {list.slice(0, 14).map((c) => (
                  <span key={c} className="rounded-md border border-border bg-secondary/60 px-2 py-1 text-[11px] font-medium text-foreground transition-colors hover:border-gold hover:bg-gold/10">{c}</span>
                ))}
                {list.length > 14 && (
                  <Link to="/countries" className="rounded-md border border-gold/40 bg-gold/10 px-2 py-1 text-[11px] font-bold text-primary hover:bg-gold/20">
                    + อีก {list.length - 14} ประเทศ →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-xl bg-primary px-5 py-4 text-center text-primary-foreground">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gold">Worldwide Coverage</span>
          <span className="ml-3 text-lg font-black">{totalCountries}+ ประเทศ</span>
          <span className="ml-3 text-xs text-primary-foreground/90">จัดส่งผ่าน DHL / FedEx / EMS ทั่วโลก</span>
        </div>
      </div>
    </section>
  );
}

/* ============== TESTIMONIALS ============== */

function Testimonials() {
  const reviews = [
    { n: "Mr. Andrei V.", r: "Russian Expat / Villa Purchase Bang Tao", t: "Команда помогла оформить POA на покупку виллы в Банг Тао — всё на русском, нотариус и почётное консульство России в Пхукете. Очень профессионально.", s: 5 },
    { n: "Mr. James K.", r: "US Digital Nomad / DTV Visa Patong", t: "Got my DTV visa sorted from Patong — Notary translation, proof of funds, health insurance — all handled on-site at my condo. Saved me a Bangkok trip.", s: 5 },
    { n: "คุณวรินทร S.", r: "จดทะเบียนสมรสไทย-เยอรมัน ที่อำเภอถลาง", t: "เตรียม Affidavit จากสถานทูตเยอรมัน แปลและรับรอง MFA แล้วจดทะเบียนที่อำเภอถลาง (สำหรับคนเชิงทะเล/ลากูน่า) ครบจบใน 10 วัน", s: 5 },
    { n: "Capt. Marco R.", r: "Yacht Captain / Royal Phuket Marina", t: "Notarized my Seaman's Book and Bareboat Charter Agreement on-site at the marina. Professional team that understands the yachting industry in Phuket.", s: 5 },
    { n: "Mr. Liu W.", r: "Chinese Investor / Condo Kata", t: "购买卡塔海滩公寓的全程文件公证，包括 FET 表格和土地厅过户，全部在普吉岛完成。沟通顺畅，价格透明。", s: 5 },
    { n: "Mrs. Helga M.", r: "German Retiree / O-A Visa Rawai", t: "Retirement Visa renewal at Phuket Immigration — health certificate, police clearance from Germany, bank letters — all translated and notarized within a week. Excellent service.", s: 5 },
  ];
  return (
    <section className="relative isolate overflow-hidden py-20 text-primary-foreground" style={{ background: "var(--gradient-hero)" }}>
      <div aria-hidden className="absolute inset-0 -z-10 opacity-[0.06]" style={{ backgroundImage: "var(--grid-pattern)", backgroundSize: "40px 40px" }} />
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="รีวิวจากลูกค้าจริง"
          title="เสียงจากลูกค้า Expat ในภูเก็ต"
          sub="ความไว้วางใจจากชุมชน Expat ทั่วเกาะ — Russian, German, French, Australian, Chinese, American — รีวิวที่ผ่านการยืนยันตัวตน"
          dark
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r) => (
            <figure key={r.n} className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-gold/50 hover:bg-white/10">
              <Quote aria-hidden className="absolute -right-2 -top-2 h-20 w-20 text-gold/10" />
              <div className="flex items-center gap-1 text-gold">
                {Array.from({ length: r.s }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-gold" />
                ))}
              </div>
              <blockquote className="mt-4 text-sm leading-relaxed text-primary-foreground/90">"{r.t}"</blockquote>
              <figcaption className="mt-5 border-t border-white/10 pt-4">
                <div className="text-sm font-bold text-primary-foreground">{r.n}</div>
                <div className="text-[11px] text-gold">{r.r}</div>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-primary-foreground/90">
            <Star className="h-5 w-5 fill-gold text-gold" />
            <strong className="text-primary-foreground">อ่านรีวิวจากแหล่งภายนอก</strong> — Google Business, LINE OA, Facebook
          </div>
          <div className="flex items-center gap-2 text-primary-foreground/90">
            <MessageCircle className="h-5 w-5 text-gold" />
            ความเห็นทั้งหมดเผยแพร่โดยได้รับอนุญาตจากลูกค้า
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== GUARANTEES ============== */

function Guarantees() {
  const items = [
    { i: ShieldCheck, t: "รับผิดชอบงานที่เราทำ", d: "หากเอกสารถูกตีกลับเพราะข้อผิดพลาดของเรา แก้ไขและดำเนินการใหม่ให้ทันทีโดยไม่คิดค่าบริการ" },
    { i: Headphones, t: "ซัพพอร์ตหลังการขาย", d: "หลังส่งมอบเอกสาร ทีมงานพร้อมตอบคำถามและช่วยเหลือต่อเนื่อง 90 วัน" },
    { i: Lock, t: "ความลับลูกค้า (NDA)", d: "ทุกเคสมีสัญญารักษาความลับ เอกสารจัดเก็บตามมาตรฐานสภาทนายความฯ" },
    { i: Clock, t: "ส่งงานตรงเวลา", d: "ระบุระยะเวลาก่อนเริ่มงานทุกครั้ง — หากล่าช้าจากเรา ลดราคา 10%" },
  ];
  return (
    <section className="bg-background py-16">
      <div className="mx-auto max-w-7xl px-4">
        <div className="rounded-3xl border-2 border-gold/40 bg-gradient-to-br from-gold/10 via-card to-card p-8 shadow-[var(--shadow-card)] md:p-12">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
              <ShieldCheck className="h-3.5 w-3.5" /> Our Guarantees
            </div>
            <h2 className="mt-4 text-3xl font-black text-primary md:text-4xl">คำมั่นบริการ 4 ข้อจากเรา</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
              เราให้คำมั่นเรื่องมาตรฐานการทำงานเป็นลายลักษณ์อักษรทุกเคส — ส่วนผลการอนุมัติเป็นดุลพินิจของหน่วยงานปลายทางเสมอ
            </p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {items.map((it) => (
              <div key={it.t} className="rounded-2xl border border-border bg-card p-5 text-center transition-all hover:-translate-y-1 hover:border-gold">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-gold">
                  <it.i className="h-7 w-7" />
                </div>
                <h3 className="mt-4 text-base font-bold text-primary">{it.t}</h3>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== KNOWLEDGE HUB ============== */

function KnowledgeHub() {
  const articles = [
    {
      i: BookOpen,
      tag: "Notary 101",
      t: "Notary Public คืออะไร? ต่างจาก Apostille อย่างไร",
      d: "อธิบายความแตกต่างระหว่างการรับรองโดยทนาย Notary, MFA Legalization และ Apostille ตามอนุสัญญา Hague 1961 พร้อมเคสตัวอย่างที่ต้องใช้แต่ละแบบ",
      r: "อ่าน 5 นาที",
    },
    {
      i: Plane,
      tag: "Visa Guide",
      t: "เอกสารต้องเตรียมสำหรับยื่นวีซ่าออสเตรเลีย",
      d: "Checklist เอกสารครบทุกประเภทวีซ่า — Student, Tourist, Work, Partner พร้อมรายละเอียดงานแปล NAATI และค่าใช้จ่ายโดยประมาณ",
      r: "อ่าน 8 นาที",
    },
    {
      i: Heart,
      tag: "Marriage",
      t: "ขั้นตอนจดทะเบียนสมรสกับชาวต่างชาติในไทย",
      d: "คู่มือฉบับสมบูรณ์ ตั้งแต่ขอใบรับรองโสดจากสถานทูต แปล รับรอง MFA จนถึงนัดที่อำเภอ ใช้เวลารวมประมาณ 14–30 วัน",
      r: "อ่าน 10 นาที",
    },
  ];
  return (
    <section className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="Knowledge Hub"
          title="คู่มือ & บทความที่ลูกค้าอ่านมากที่สุด"
          sub="รวมความรู้เกี่ยวกับ Notary, การรับรองเอกสาร และการเตรียมตัวยื่นวีซ่า — เขียนโดยทีมทนายมืออาชีพ"
        />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {articles.map((a) => (
            <article key={a.t} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold">
              <div className="relative flex aspect-[16/9] items-center justify-center overflow-hidden" style={{ background: "var(--gradient-hero)" }}>
                <div aria-hidden className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "var(--grid-pattern)", backgroundSize: "24px 24px" }} />
                <a.i className="h-20 w-20 text-gold/80 transition-transform group-hover:scale-110" />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-foreground">
                  <Newspaper className="h-3 w-3" /> {a.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="text-lg font-bold text-primary group-hover:text-gold">{a.t}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{a.d}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> {a.r}
                  </span>
                  <a href="https://www.thainotary.co.th" target="_blank" rel="noopener" className="inline-flex items-center gap-1 font-bold text-primary group-hover:text-gold">
                    อ่านต่อ <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className="mt-8 text-center">
          <a href="https://www.thainotary.co.th" target="_blank" rel="noopener" className="inline-flex items-center gap-2 rounded-xl border-2 border-primary bg-transparent px-6 py-3 text-sm font-bold text-primary hover:bg-primary hover:text-primary-foreground">
            <Lightbulb className="h-4 w-4" /> ดูบทความทั้งหมดที่ thainotary.co.th <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============== POLICE CLEARANCE TYPES ============== */

function PoliceTypes() {
  return (
    <section id="police-clearance" className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="ตรวจประวัติอาชญากร & Police Clearance"
          title="บริการตรวจประวัติครบทุกประเภท — ใช้ในไทยและทั่วโลก"
          sub="ครอบคลุมทั้งคนไทยในประเทศ คนไทยที่อยู่ต่างประเทศ และชาวต่างชาติที่ต้องการเอกสารใช้ในไทย"
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {POLICE_CLEARANCE_TYPES.map((g, idx) => {
            const Icon = idx === 0 ? Search : Globe2;
            return (
              <div key={g.group} className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-gold">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-bold text-primary">{g.group}</h3>
                  <span className="ml-auto rounded-full bg-gold/15 px-2.5 py-0.5 text-[10px] font-bold text-gold-foreground">{g.items.length}</span>
                </div>
                <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                  {g.items.slice(0, 8).map((it) => (
                    <li key={it} className="flex items-start gap-2 text-[13px] text-foreground">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
                      <span>{it}</span>
                    </li>
                  ))}
                  {g.items.length > 8 && (
                    <li className="text-[12px] font-bold sm:col-span-2">
                      <Link to="/police-clearance" className="text-primary hover:text-gold">
                        + อีก {g.items.length - 8} รายการ — ดูบริการตรวจประวัติทั้งหมด →
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============== EMBASSY DIRECTORY ============== */

function EmbassyDirectory() {
  const embassyEntries = toSafeGroupedStringEntries(EMBASSIES);
  const total = embassyEntries.reduce((sum, [, list]) => sum + list.length, 0);
  return (
    <section id="embassies" className="bg-background py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="Embassy Directory"
          title={`รับรองสถานทูตครบ ${total}+ ประเทศ — ส่งจากภูเก็ตไม่ต้องเดินทาง`}
          sub="กงสุลกิตติมศักดิ์ 22 ประเทศที่ตั้งสำนักงานในเกาะภูเก็ตทำได้ในเกาะ — ประเทศอื่นๆ เราส่งคูเรียร์ไปสถานทูตที่กรุงเทพ DHL/FedEx ไป-กลับ 5–10 วันทำการ"
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {embassyEntries.map(([region, list]) => (
            <div key={region} className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-gold">
                  <Landmark className="h-5 w-5" />
                </div>
                <h3 className="text-base font-bold text-primary">{region}</h3>
                <span className="ml-auto rounded-full bg-gold/15 px-2.5 py-0.5 text-[10px] font-bold text-gold-foreground">{list.length}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {list.slice(0, 12).map((e) => (
                  <span key={e} className="rounded-md border border-border bg-secondary/60 px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:border-gold hover:bg-gold/10">{e}</span>
                ))}
                {list.length > 12 && (
                  <Link to="/embassies" className="rounded-md border border-gold/40 bg-gold/10 px-2.5 py-1 text-[11px] font-bold text-primary hover:bg-gold/20">
                    + อีก {list.length - 12} ประเทศ →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-muted-foreground">
          สถานทูตที่ไม่อยู่ในรายการ? <a href={`tel:${COMPANY.phoneIntl}`} className="font-bold text-primary hover:text-gold">โทร {COMPANY.phone} ปรึกษาฟรี →</a>
        </p>
      </div>
    </section>
  );
}

/* ============== VISA CATALOG ============== */

function VisaCatalog() {
  const [tab, setTab] = useState<"outbound" | "inbound">("outbound");
  const data = tab === "outbound" ? OUTBOUND_VISA : INBOUND_THAI_VISA;
  return (
    <section id="visa-catalog" className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="Visa Catalog"
          title="แค็ตตาล็อกวีซ่าครบทุกประเทศ ทุกประเภท"
          sub="ปรึกษาวีซ่าทั่วโลกสำหรับคนไทย และวีซ่าประเทศไทยทุกประเภทสำหรับชาวต่างชาติ"
        />
        <div className="mt-10 flex justify-center gap-2">
          <button
            onClick={() => setTab("outbound")}
            className={`rounded-full px-6 py-2.5 text-sm font-bold transition-all ${tab === "outbound" ? "bg-primary text-primary-foreground shadow" : "border border-border bg-card text-foreground hover:border-gold"}`}
          >
            🛫 คนไทย → ต่างประเทศ
          </button>
          <button
            onClick={() => setTab("inbound")}
            className={`rounded-full px-6 py-2.5 text-sm font-bold transition-all ${tab === "inbound" ? "bg-primary text-primary-foreground shadow" : "border border-border bg-card text-foreground hover:border-gold"}`}
          >
            🛬 ต่างชาติ → ไทย
          </button>
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {data.map((g) => {
            const title = "country" in g ? g.country : g.group;
            return (
              <div key={title} className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold hover:shadow-[var(--shadow-gold-glow)]">
                <div className="flex items-center gap-2">
                  <Plane className="h-4 w-4 text-gold" />
                  <h3 className="text-sm font-bold text-primary">{title}</h3>
                  <span className="ml-auto text-[10px] font-bold text-muted-foreground">{g.types.length}</span>
                </div>
                <ul className="mt-3 space-y-1.5">
                  {g.types.slice(0, 6).map((t) => (
                    <li key={t} className="flex items-start gap-2 text-[12px] leading-snug text-foreground">
                      <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-gold" />
                      <span>{t}</span>
                    </li>
                  ))}
                  {g.types.length > 6 && (
                    <li className="pt-1 text-[12px] font-bold">
                      <Link to="/visa/thai" className="text-primary hover:text-gold">
                        + อีก {g.types.length - 6} ประเภท →
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============== PHUKET INFRASTRUCTURE — โครงสร้างพื้นฐานบนเกาะ ============== */

function ProvinceCoverage() {
  const honoraryConsuls = toSafeGroupedStringEntries(PHUKET_HONORARY_CONSULS).flatMap(([, items]) => items);
  const groups: { title: string; items: readonly string[]; icon: typeof MapPin; note: string }[] = [
    { title: "โรงพยาบาลนานาชาติในภูเก็ต", items: PHUKET_INTL_HOSPITALS, icon: ShieldCheck, note: "รับเอกสารใบรับรองแพทย์เพื่อยื่นวีซ่า/ประกัน" },
    { title: "หน่วยงานราชการในจังหวัดภูเก็ต", items: PHUKET_GOV_OFFICES, icon: Landmark, note: "ยื่นเอกสารแทนลูกค้าทุกหน่วยงานในเกาะ" },
    { title: "กงสุลกิตติมศักดิ์ในภูเก็ต", items: honoraryConsuls, icon: Globe2, note: `${PHUKET_CONSUL_TOTAL} ประเทศ — ไม่ต้องบินไปกรุงเทพ` },
    { title: "อำเภอในจังหวัดภูเก็ต", items: PHUKET_DISTRICTS, icon: MapPin, note: "เมืองภูเก็ต • กะทู้ • ถลาง" },
  ];
  return (
    <section id="phuket-infra" className="bg-background py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHead
          eyebrow="โครงสร้างพื้นฐานบนเกาะ"
          title="ทำงานร่วมกับทุกหน่วยงานสำคัญในจังหวัดภูเก็ต"
          sub="โรงพยาบาลนานาชาติ หน่วยงานราชการ และกงสุลกิตติมศักดิ์ 22 ประเทศ — เราเป็นตัวกลางที่รู้จักทุกขั้นตอนในเกาะ"
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {groups.map((g) => {
            const Icon = g.icon;
            return (
              <div key={g.title} className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-gold">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-bold text-primary">{g.title}</h3>
                  <span className="ml-auto rounded-full bg-gold/15 px-2.5 py-0.5 text-[10px] font-bold text-gold-foreground">{g.items.length}</span>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">{g.note}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {g.items.slice(0, 12).map((it) => (
                    <span key={it} className="rounded-md border border-border bg-secondary/60 px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:border-gold hover:bg-gold/10">{it}</span>
                  ))}
                  {g.items.length > 12 && (
                    <span className="rounded-md border border-gold/40 bg-gold/10 px-2.5 py-1 text-[11px] font-bold text-primary">
                      + อีก {g.items.length - 12} รายการ
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-gold/30 bg-gold/5 px-4 py-3 text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gold">สำนักงานในเกาะ</div>
            <div className="mt-1 text-xl font-black text-primary">4 สาขา</div>
            <div className="text-[11px] text-muted-foreground">Phuket Town • Patong • Laguna • Chalong</div>
          </div>
          <div className="rounded-xl border border-gold/30 bg-gold/5 px-4 py-3 text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gold">On-site ทั่วเกาะ</div>
            <div className="mt-1 text-xl font-black text-primary">3/3 อำเภอ</div>
            <div className="text-[11px] text-muted-foreground">เมือง • กะทู้ • ถลาง</div>
          </div>
          <div className="rounded-xl border border-gold/30 bg-gold/5 px-4 py-3 text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gold">กงสุลกิตติมศักดิ์</div>
            <div className="mt-1 text-xl font-black text-primary">{PHUKET_CONSUL_TOTAL} ประเทศ</div>
            <div className="text-[11px] text-muted-foreground">รับรองเอกสารโดยไม่ต้องบินไปกรุงเทพ</div>
          </div>
        </div>
      </div>
    </section>
  );
}


function Facebook(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <title>Facebook</title>
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.99 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.128 22 16.99 22 12z"/>
    </svg>
  );
}
